import { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { keycloak } from './keycloak';
import { setApiToken, setEchoIdToken } from '@/services/api-client';
import { parseVertexClaims } from './vertex-claims';
import { useWorkspaceStore } from '@/stores/workspace-store';

const DEV_AUTH_BYPASS = import.meta.env.VITE_DEV_AUTH_BYPASS === 'true';

/** Minimum token validity in seconds before refresh */
const MIN_TOKEN_VALIDITY_SEC = 30;

/** Interval between token refresh checks in ms */
const TOKEN_REFRESH_INTERVAL_MS = 10_000;

interface AuthState {
  authenticated: boolean;
  username: string;
  token: string;
  roles: string[];
}

interface AuthContextValue extends AuthState {
  /** Log the user out and redirect to Keycloak logout */
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/** Access the current auth context. Throws if used outside AuthProvider. */
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
}

/** Extracts realm roles from the Keycloak token */
function extractRoles(): string[] {
  const realmAccess = keycloak.tokenParsed?.realm_access;
  return realmAccess?.roles ?? [];
}

/** Wraps children with Keycloak authentication. Redirects to login if unauthenticated. */
export function AuthProvider({ children }: { children: React.ReactNode }): React.JSX.Element {
  const [authState, setAuthState] = useState<AuthState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const refreshTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (DEV_AUTH_BYPASS) {
      setApiToken('dev-bypass');
      useWorkspaceStore.getState().setPlatformAdmin(true);
      setAuthState({
        authenticated: true,
        username: 'dev-user',
        token: 'dev-bypass',
        roles: ['admin', 'operator', 'platform-admin'],
      });
      return;
    }

    initKeycloak();
    return () => {
      if (refreshTimerRef.current) {
        clearInterval(refreshTimerRef.current);
      }
    };
  }, []);

  /** Initialize Keycloak and set up token refresh */
  async function initKeycloak(): Promise<void> {
    try {
      const authenticated = await keycloak.init({
        onLoad: 'login-required',
        checkLoginIframe: false,
      });

      if (!authenticated) {
        return;
      }

      const token = keycloak.token ?? '';
      setApiToken(token);
      setEchoIdToken(keycloak.idToken ?? '');
      // Seed the workspace identity from token claims for instant display; the
      // authoritative manifest from /api/config replaces it moments later.
      useWorkspaceStore.getState().seedFromClaims(
        parseVertexClaims(keycloak.tokenParsed as Record<string, unknown> | undefined),
      );
      useWorkspaceStore.getState().setPlatformAdmin(extractRoles().includes('platform-admin'));
      setAuthState({
        authenticated: true,
        username: keycloak.tokenParsed?.preferred_username ?? '',
        token,
        roles: extractRoles(),
      });

      startTokenRefresh();
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setError(`Authentication failed: ${message}`);
    }
  }

  /** Periodically refresh the token before it expires */
  function startTokenRefresh(): void {
    refreshTimerRef.current = setInterval(async () => {
      try {
        const refreshed = await keycloak.updateToken(MIN_TOKEN_VALIDITY_SEC);
        if (refreshed) {
          const newToken = keycloak.token ?? '';
          setApiToken(newToken);
          setEchoIdToken(keycloak.idToken ?? '');
          setAuthState(prev =>
            prev
              ? { ...prev, token: newToken, roles: extractRoles() }
              : prev,
          );
        }
      } catch {
        keycloak.login();
      }
    }, TOKEN_REFRESH_INTERVAL_MS);
  }

  const logout = useCallback(() => {
    if (DEV_AUTH_BYPASS) {
      window.location.reload();
      return;
    }
    keycloak.logout({ redirectUri: window.location.origin });
  }, []);

  if (error) {
    return (
      <div style={{ display: 'grid', placeItems: 'center', height: '100vh', fontFamily: 'sans-serif' }}>
        <div style={{ textAlign: 'center' }}>
          <h1 style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>Authentication Error</h1>
          <p style={{ color: '#888' }}>{error}</p>
        </div>
      </div>
    );
  }

  if (!authState) {
    return (
      <div style={{ display: 'grid', placeItems: 'center', height: '100vh', fontFamily: 'sans-serif' }}>
        <p style={{ color: '#888' }}>Authenticating...</p>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ ...authState, logout }}>
      {children}
    </AuthContext.Provider>
  );
}
